import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { fetchData } from "../../services/api";
import { alertNotification } from "../../services/notications";
import { sortMaterialsByName } from "../../services/sorting";
import { showBar, hideBar } from "../../store/reducers/sidebar";
import { ZBUT_MODULE_ID } from "../../services/zbut";
import MaterialsNotFound from "../Errors/MaterialsNotFound";

import Player from "../../components/Player";
import PdfViewer from "../../components/PDFViewer";
import TestPreview from "../../components/AddTestsBody/TestPreview";
import FileViewer from "react-file-viewer";

import "./ModulePage.scss";

const ModulePage = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const moduleId = searchParams.get("id");
  const modules = useSelector((state) => state.modules);
  const { user } = useSelector((state) => state.user);
  const locked = useSelector((state) => state.zbut.locked);

  const userRole = user?.role_name?.toLowerCase();
  const isAdmin = userRole === "admin" || userRole === "administrator";
  // Докато "ЗБУТ" не е попълнен за деня, другите модули не се показват - дори
  // от кеширания (redux-persist) списък. App.js прави и пренасочването.
  const isBlocked = locked && Number(moduleId) !== ZBUT_MODULE_ID;

  useEffect(() => {
    if (isBlocked) {
      setMaterials([]);
      return;
    }
    const module = modules?.modules.find((module) => module.id === Number(moduleId));
    // Sorted locally too: the modules slice is persisted, so a stale unsorted
    // array is rehydrated and rendered before fetchModules resolves.
    setMaterials(sortMaterialsByName(module?.materials || []));
  }, [modules, isBlocked])

  const [materials, setMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState({});
  const [extension, setExtension] = useState("");
  const wrapper = useRef();

  const selectHandler = (e) => {
    e = e.target || e;
    if (!e || !e.id) return;

    wrapper.current.childNodes.forEach((material) => {
      if (Number(material.id) !== Number(e.id)) {
        material.classList.remove("selected");
      } else material.classList.add("selected");
    });

    const material = materials.find(
      (material) => material.material_id === Number(e.id)
    );

    const target = material.content?.split(".");
    let ext;
    if (target?.length > 0) {
      ext = target[1];
    } else {
      ext = "";
    }

    setExtension(ext);
    setSelectedMaterial(material);
  };

  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState({});

  const openPreview = async () => {
    try {
      const response = await fetchData(`/tests/preview?moduleId=${moduleId}`);

      if (response.status !== 201) {
        return alertNotification(response.data.message);
      }

      setPreviewData(response.data.data);
      setShowPreview(true);
    } catch (err) {
      alertNotification("Тестът не можа да бъде зареден!");
    }
  };

  useEffect(() => {
    if (
      !materials ||
      !wrapper ||
      !wrapper.current ||
      !wrapper.current.childNodes
    ) {
      return;
    }

    selectHandler(wrapper.current.childNodes[0]);
  }, [materials]);

  useEffect(() => {
    dispatch(hideBar());

    return () => {
      dispatch(showBar());
    };
  });

  const onError = (e) => {
    return <div>Error in loading! {e.target.value}</div>;
  };

  if (isBlocked) return null;

  return (
    <div className="module-page d-flex flex-row">
      {selectedMaterial && selectedMaterial.material_id && (
        <div className="module-content">
          {Number(selectedMaterial.material_type) === 1 ? (
            <div
              className="content-textarea"
              dangerouslySetInnerHTML={{ __html: selectedMaterial.content }}
              onDragStart={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
            />
          ) : Number(selectedMaterial.material_type) === 2 ? (
            <Player props={{ videoId: selectedMaterial.content }} />
          ) : Number(selectedMaterial.material_type) === 3 ? (
            extension === "pdf" ? (
              <PdfViewer
                props={{
                  filePath: `${process.env.REACT_APP_BACKEND_URL}${process.env.REACT_APP_BACKEND_PORT ? `:${process.env.REACT_APP_BACKEND_PORT}` : ''}${process.env.NODE_ENV === 'development' ? '/api' : ''}/${selectedMaterial.content}`,
                }}
              />
            ) : extension === "doc" || extension === "docx" ? (
              <div
                onDragStart={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
              >
                <FileViewer
                  fileType={extension}
                  filePath={`${process.env.REACT_APP_BACKEND_URL}${process.env.REACT_APP_BACKEND_PORT ? `:${process.env.REACT_APP_BACKEND_PORT}` : ''}${process.env.NODE_ENV === 'development' ? '/api' : ''}/${selectedMaterial.content}`}
                  onError={onError}
                  onDragStart={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                />
              </div>
            ) : extension === "ppt" || extension === "pptx" ? (
              <div></div>
            ) : (
              ""
            )
          ) : (
            ""
          )}
        </div>
      )}
      {materials.length > 0 ? (
        <div className="material-sidebar">
          <Link to="/my-courses" className="sidebar-header">
            <i className="bi bi-arrow-left" />
            <span>Обучения</span>
          </Link>
          <div className="sidebar-content">
            <div className="lessons">
              <div className="content" ref={wrapper}>
                {materials.map((material, index) => {
                  return (
                    <div
                      className="material"
                      key={index}
                      id={material.material_id}
                      onClick={selectHandler}
                    >
                      {material.material_name}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {isAdmin ? (
            <button
              className="card-btn btn btn-primary buttons"
              onClick={openPreview}
            >
              Прегледай теста
            </button>
          ) : (
            <Link
              to={`/test?moduleId=${moduleId}`}
              className="card-btn btn btn-primary buttons"
            >
              Към изпит
            </Link>
          )}
        </div>
      ) : (
        materials.length !== 1 && <MaterialsNotFound />
      )}
      {showPreview && (
        <TestPreview
          show={showPreview}
          testData={previewData}
          hide={() => {
            setShowPreview(false);
          }}
        />
      )}
    </div>
  );
};

export default ModulePage;
